import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ZoomIn, ZoomOut, RotateCcw, X, Check } from 'lucide-react';

export interface ImageCropModalProps {
  open: boolean;
  imageUrl: string;
  title?: string;
  outputSize?: number;
  cropW: number;
  cropH: number;
  onConfirm: (croppedDataUrl: string) => void | Promise<void>;
  onCancel: () => void;
}

/**
 * Reusable zoom/pan/crop modal. Renders the visible crop region onto an
 * off-screen canvas and returns a flattened PNG data URL via onConfirm —
 * so the result is a real image (works in email/anywhere), not a CSS transform.
 *
 * Copied from the existing avatar/company-logo crop modal in AccountSettings.tsx
 * (kept untouched there) so this new usage carries zero risk to that flow.
 */
export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  open,
  imageUrl,
  title = 'Adjust Image',
  outputSize = 400,
  cropW,
  cropH,
  onConfirm,
  onCancel,
}) => {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [imgLoaded, setImgLoaded] = useState(false);
  const [minScale, setMinScale] = useState(1);
  // True while onConfirm's returned promise (e.g. uploading + saving a
  // secondary photo) is in flight, so Apply can show a spinner instead of
  // leaving the modal looking frozen for the ~seconds a real upload+save takes.
  const [isSaving, setIsSaving] = useState(false);

  const cropContainerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const dragging = useRef(false);
  const lastPointer = useRef({ x: 0, y: 0 });
  const lastPinchDist = useRef<number | null>(null);
  const scaleRef = useRef(1);
  const naturalRef = useRef({ w: 1, h: 1 });
  const loadedUrlRef = useRef('');

  /* ── helpers ── */
  const getCoverScale = (w: number, h: number) =>
    Math.max(cropW / w, cropH / h);

  // Cap zoom at 50% of the photo's native resolution — zooming in further
  // than that stretches fewer source pixels over more screen pixels and
  // starts looking visibly blurry/pixelated. Never go below the cover scale
  // though, or the frame couldn't be filled at all for lower-resolution photos.
  const MAX_ZOOM_OF_NATIVE = 0.5;
  const getMaxScale = (w: number, h: number) => {
    const cover = getCoverScale(w, h);
    // If the cover scale (the minimum zoom needed to fill the frame at all)
    // is already at or past the 50% cap, the cap is unreachable — the photo
    // is simply too low-resolution for this crop box. Returning `cover`
    // unchanged here would make min === max and freeze the zoom slider and
    // +/- buttons entirely. Give a little headroom above cover instead so
    // the controls stay usable, even though the result is a slightly softer
    // crop for this one low-resolution photo.
    if (cover >= MAX_ZOOM_OF_NATIVE) return cover * 1.25;
    return MAX_ZOOM_OF_NATIVE;
  };

  const clampOff = (ox: number, oy: number, sc: number, w: number, h: number) => {
    const iw = w * sc;
    const ih = h * sc;
    const maxX = Math.max(0, (iw - cropW) / 2);
    const maxY = Math.max(0, (ih - cropH) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, ox)),
      y: Math.max(-maxY, Math.min(maxY, oy)),
    };
  };

  const applyInit = (w: number, h: number) => {
    const cover = Math.max(cropW / w, cropH / h);
    naturalRef.current = { w, h };
    scaleRef.current = cover;
    setMinScale(cover);
    setScale(cover);
    setOffset({ x: 0, y: 0 });
    setImgLoaded(true);
  };

  /* ── when modal opens, load image via JS Image() for reliable dimensions ── */
  useEffect(() => {
    if (!open || !imageUrl) return;
    if (loadedUrlRef.current === imageUrl) return;

    setImgLoaded(false);
    loadedUrlRef.current = imageUrl;

    const probe = new window.Image();
    // Must match the crossOrigin mode of the visible <img> below (which needs
    // it set for canvas export to work on cross-origin URLs) — otherwise this
    // probe resolves from a different cache entry and can report "loaded"
    // before the actual crossOrigin fetch for the visible <img> has finished,
    // letting Apply be clicked while that element is still empty.
    probe.crossOrigin = 'anonymous';
    probe.onload = () => applyInit(probe.naturalWidth, probe.naturalHeight);
    probe.onerror = () => console.error('ImageCropModal: image failed to load');
    probe.src = imageUrl;
  }, [open, imageUrl]);

  /* ── reset when modal fully closes ── */
  useEffect(() => {
    if (!open) {
      loadedUrlRef.current = '';
      setImgLoaded(false);
      setScale(1);
      setMinScale(1);
      setOffset({ x: 0, y: 0 });
      naturalRef.current = { w: 1, h: 1 };
      scaleRef.current = 1;
      setIsSaving(false);
    }
  }, [open]);

  /* ── wheel zoom ── */
  useEffect(() => {
    const el = cropContainerRef.current;
    if (!el || !imgLoaded) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const { w, h } = naturalRef.current;
      const cover = getCoverScale(w, h);
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setScale(prev => {
        const next = Math.max(cover, Math.min(getMaxScale(w, h), prev + delta));
        scaleRef.current = next;
        setOffset(o => clampOff(o.x, o.y, next, w, h));
        return next;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [imgLoaded]);

  /* ── zoom buttons ── */
  const resetTransform = () => {
    const { w, h } = naturalRef.current;
    const cover = getCoverScale(w, h);
    scaleRef.current = cover;
    setScale(cover);
    setOffset({ x: 0, y: 0 });
  };

  const changeZoom = (delta: number) => {
    const { w, h } = naturalRef.current;
    const cover = getCoverScale(w, h);
    setScale(prev => {
      const next = Math.max(cover, Math.min(getMaxScale(w, h), prev + delta));
      scaleRef.current = next;
      setOffset(o => clampOff(o.x, o.y, next, w, h));
      return next;
    });
  };

  /* ── pointer drag ── */
  const onPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragging.current = true;
    lastPointer.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;
    const dx = e.clientX - lastPointer.current.x;
    const dy = e.clientY - lastPointer.current.y;
    lastPointer.current = { x: e.clientX, y: e.clientY };
    const { w, h } = naturalRef.current;
    setOffset(prev => clampOff(prev.x + dx, prev.y + dy, scaleRef.current, w, h));
  };
  const onPointerUp = () => { dragging.current = false; };

  /* ── pinch zoom ── */
  const onTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length !== 2) return;
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    const dist = Math.hypot(dx, dy);
    if (lastPinchDist.current !== null) {
      const { w, h } = naturalRef.current;
      const cover = getCoverScale(w, h);
      const ratio = dist / lastPinchDist.current;
      setScale(prev => {
        const next = Math.max(cover, Math.min(getMaxScale(w, h), prev * ratio));
        scaleRef.current = next;
        setOffset(o => clampOff(o.x, o.y, next, w, h));
        return next;
      });
    }
    lastPinchDist.current = dist;
  };
  const onTouchEnd = () => { lastPinchDist.current = null; };

  /* ── canvas export ── */
  const handleConfirm = async () => {
    const outputH = Math.round(outputSize * (cropH / cropW));
    const canvas = document.createElement('canvas');
    canvas.width = outputSize;
    canvas.height = outputH;
    const ctx = canvas.getContext('2d')!;
    const { w, h } = naturalRef.current;
    const iw = w * scale;
    const ih = h * scale;
    const imgLeft = cropW / 2 - iw / 2 + offset.x;
    const imgTop = cropH / 2 - ih / 2 + offset.y;
    ctx.drawImage(
      imgRef.current!,
      -imgLeft / scale, -imgTop / scale,
      cropW / scale, cropH / scale,
      0, 0, outputSize, outputH,
    );
    try {
      setIsSaving(true);
      // Some callers (hero image) update local state only and return
      // synchronously; others (secondary photos) upload + PATCH the
      // property and return a promise — awaiting a non-promise resolves
      // on the next microtask, so this is a no-op overlay flash for the
      // synchronous case and a real spinner for the async one.
      await onConfirm(canvas.toDataURL('image/png'));
    } finally {
      setIsSaving(false);
    }
  };

  /* ── layout ── */
  const { w, h } = naturalRef.current;
  const iw = w * scale;
  const ih = h * scale;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v && !isSaving) onCancel(); }}>
      <DialogContent
        className="w-[calc(100vw-24px)] sm:max-w-xl md:max-w-2xl rounded-3xl p-0 overflow-hidden border border-slate-200 bg-white shadow-2xl"
        onInteractOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => { if (isSaving) e.preventDefault(); }}
      >
        <DialogHeader className="px-5 sm:px-6 pt-5 sm:pt-6 pb-3 sm:pb-4 border-b bg-gradient-to-r from-slate-50 to-white">
          <DialogTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
            {title}
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            Drag to reposition · scroll or pinch to zoom
          </p>
        </DialogHeader>

        <div className="flex flex-col items-center gap-5 px-5 sm:px-6 py-5 sm:py-6 bg-white">
          <div className="w-full rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-6">
            <div className="flex flex-col items-center gap-4">

              {/* ── Crop preview ── */}
              <div
                ref={cropContainerRef}
                className="relative overflow-hidden select-none touch-none flex-shrink-0"
                style={{
                  width: cropW,
                  height: cropH,
                  borderRadius: '16px',
                  background: '#94a3b8',
                  cursor: 'grab',
                }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerLeave={onPointerUp}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                {/* Crosshair */}
                <div className="absolute inset-0 pointer-events-none z-10">
                  <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/40 -translate-x-px" />
                  <div className="absolute top-1/2 left-0 right-0 h-px bg-white/40 -translate-y-px" />
                </div>

                <img
                  ref={imgRef}
                  src={imageUrl}
                  alt=""
                  crossOrigin="anonymous"
                  draggable={false}
                  style={{
                    position: 'absolute',
                    width: imgLoaded ? iw : 0,
                    height: imgLoaded ? ih : 0,
                    left: imgLoaded ? cropW / 2 - iw / 2 + offset.x : -9999,
                    top: imgLoaded ? cropH / 2 - ih / 2 + offset.y : -9999,
                    opacity: imgLoaded ? 1 : 0,
                    userSelect: 'none',
                    pointerEvents: 'none',
                    maxWidth: 'none',
                    maxHeight: 'none',
                  }}
                />

                {/* Spinner while loading */}
                {!imgLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center z-20">
                    <div className="h-8 w-8 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                  </div>
                )}

                {/* Overlay while Apply's upload/save is in flight */}
                {isSaving && (
                  <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 bg-black/60">
                    <div className="h-8 w-8 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                    <span className="text-xs font-medium text-white">Saving…</span>
                  </div>
                )}
              </div>

              {/* ── Zoom controls ── */}
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={() => changeZoom(-0.15)}
                  disabled={!imgLoaded}
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-colors shadow-sm disabled:opacity-40"
                >
                  <ZoomOut className="h-4 w-4" />
                </button>
                <input
                  type="range"
                  min={Math.round(minScale * 100)}
                  max={Math.round(getMaxScale(w, h) * 100)}
                  step={1}
                  value={Math.round(scale * 100)}
                  disabled={!imgLoaded}
                  onChange={e => {
                    const { w, h } = naturalRef.current;
                    const cover = getCoverScale(w, h);
                    const next = Math.max(cover, Math.min(getMaxScale(w, h), Number(e.target.value) / 100));
                    scaleRef.current = next;
                    setScale(next);
                    setOffset(o => clampOff(o.x, o.y, next, w, h));
                  }}
                  className="w-40 sm:w-56 accent-blue-600 cursor-pointer disabled:opacity-40"
                />
                <button
                  onClick={() => changeZoom(0.15)}
                  disabled={!imgLoaded}
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-colors shadow-sm disabled:opacity-40"
                >
                  <ZoomIn className="h-4 w-4" />
                </button>
                <button
                  onClick={resetTransform}
                  disabled={!imgLoaded}
                  title="Reset"
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-colors shadow-sm disabled:opacity-40"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
              </div>

              <p className="text-[11px] text-slate-400 text-center">
                Position your image inside the frame, then click Apply.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="px-5 sm:px-6 py-4 border-t bg-slate-50 flex flex-row gap-2 justify-end">
          <Button variant="outline" size="sm" onClick={onCancel} disabled={isSaving} className="gap-1.5">
            <X className="h-3.5 w-3.5" /> Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={!imgLoaded || isSaving}
            className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isSaving ? (
              <>
                <div className="h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Check className="h-3.5 w-3.5" /> Apply
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropModal;
